/**
 * DataTable — an extracted array of records inside the Structured tab
 * (spec §9.6).
 *
 * Hand-built rather than AG Grid on purpose: it lives in a ~520px reading
 * column, rows carry grouped subtotals, inline tags and an expandable detail
 * list, and goTo() needs to scroll a specific row into view inside the tab's
 * own scroll area — a document-reading list, not a data grid.
 */
import { Fragment, useMemo } from "react";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import SearchRounded from "@mui/icons-material/SearchRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import FindInPageOutlined from "@mui/icons-material/FindInPageOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import { level3 } from "../../../../../../../theme/colors";
import { APP_FONT, FOCUS_RING, INK, LINE, LINE_SOFT, SUB, SURFACE, domId, flashSx, toneOf, toolsSx } from "./tokens";
import { LinkButton, Tag, Blocks } from "./blocks";
import { fmtMoney } from "../format/registry";

export const DEFAULT_TABLE_STATE = { query: "", view: "grouped", toggled: [], rows: [], limit: 50 };

const pct = (share) => (share === null || share === undefined ? "" : `${Number((share * 100).toFixed(1))}%`);

const rowText = (r) =>
  [r.title, r.subtitle, ...r.tags.map((t) => t.label), ...Object.values(r.cells).map((c) => c.display)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const isGroupOpen = (group, st) =>
  group.kind === "empty" ? st.toggled.includes(group.id) : !st.toggled.includes(group.id);

export default function DataTable({ table, ui, actions }) {
  const st = { ...DEFAULT_TABLE_STATE, ...(ui.tables[table.path] || {}) };
  const set = (patch) => actions.setTable(table.path, patch);
  const hasValue = !!table.valueKey;
  const cols = table.visibleColumns.map((k) => table.columns.find((c) => c.key === k)).filter(Boolean);
  const grid = [
    "minmax(0, 1fr)",
    ...cols.map(() => "minmax(64px, auto)"),
    ...(hasValue ? ["56px", "minmax(72px, auto)"] : []),
  ].join(" ");
  const grouped = !!table.groups && st.view === "grouped";
  const q = st.query.trim().toLowerCase();
  const matches = useMemo(
    () => (q ? new Set(table.rows.filter((r) => rowText(r).includes(q)).map((r) => r.path)) : null),
    [q, table.rows],
  );
  const visible = (r) => !matches || matches.has(r.path);

  const toggleGroup = (g) =>
    set({ toggled: st.toggled.includes(g.id) ? st.toggled.filter((x) => x !== g.id) : [...st.toggled, g.id] });
  const toggleRow = (r) =>
    set({ rows: st.rows.includes(r.path) ? st.rows.filter((x) => x !== r.path) : [...st.rows, r.path] });

  const renderRow = (r, { labelOverride, nested } = {}) => {
    const open = st.rows.includes(r.path);
    const canOpen = r.detail.length > 0;
    const flash = ui.flash?.path === r.path ? ui.flash.mode : undefined;
    return (
      <Fragment key={r.path}>
        <Box
          id={domId(r.path)}
          data-flash={flash}
          role={canOpen ? "button" : undefined}
          tabIndex={canOpen ? 0 : undefined}
          aria-expanded={canOpen ? open : undefined}
          onClick={canOpen ? () => toggleRow(r) : undefined}
          onKeyDown={
            canOpen
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleRow(r);
                  }
                }
              : undefined
          }
          sx={{
            display: "grid",
            gridTemplateColumns: grid,
            columnGap: 1.5,
            alignItems: "center",
            pl: nested ? 3.5 : 1.5,
            pr: 1.5,
            py: 1,
            borderTop: `1px solid ${LINE_SOFT}`,
            cursor: canOpen ? "pointer" : "default",
            opacity: r.muted ? 0.6 : 1,
            outline: "none",
            "&:hover": { backgroundColor: SURFACE },
            "&:focus-visible": { ...FOCUS_RING, outlineOffset: "-2px" },
            ...toolsSx,
            ...flashSx,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
              <Box sx={{ fontSize: 13, fontWeight: 500, color: INK }}>{labelOverride || r.title}</Box>
              {r.tags.map((t) => (
                <Tag key={t.label} label={t.label} tone={t.tone} />
              ))}
              {ui.canShowInText && r.findQuery && actions.showInText ? (
                <Tooltip title={`Show ${r.title} in text`}>
                  <IconButton
                    className="sv-tools"
                    size="small"
                    aria-label={`Show ${r.title} in text`}
                    onClick={(e) => {
                      e.stopPropagation();
                      actions.showInText(r.findQuery);
                    }}
                    sx={{ p: 0.25, color: SUB }}
                  >
                    <FindInPageOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            {r.subtitle ? (
              <Box sx={{ fontSize: 12, color: SUB, mt: 0.25, overflowWrap: "anywhere" }}>{r.subtitle}</Box>
            ) : null}
          </Box>
          {cols.map((c) => (
            <Box key={c.key} sx={{ fontSize: 12.5, color: r.cells[c.key]?.display ? INK : SUB, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {r.cells[c.key]?.display ?? "—"}
            </Box>
          ))}
          {hasValue ? (
            <>
              <Box sx={{ fontSize: 12.5, color: SUB, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {pct(r.share)}
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: r.empty ? 400 : 600,
                  color: r.empty ? SUB : INK,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {r.valueDisplay}
              </Box>
            </>
          ) : null}
        </Box>
        {open ? (
          <Box sx={{ pl: nested ? 3.5 : 1.5, pr: 1, pb: 1, backgroundColor: SURFACE }}>
            <Blocks blocks={r.detail} ui={ui} actions={actions} />
          </Box>
        ) : null}
      </Fragment>
    );
  };

  const paged = (rows, key) => {
    const limit = st.limit;
    const shown = q ? rows : rows.slice(0, limit);
    return (
      <>
        {shown.map((r) => renderRow(r, { nested: key !== "flat" }))}
        {!q && rows.length > limit ? (
          <Box sx={{ display: "flex", gap: 1.5, px: 1.5, py: 1, borderTop: `1px solid ${LINE_SOFT}` }}>
            <LinkButton onClick={() => set({ limit: limit + 50 })}>Show 50 more</LinkButton>
            <LinkButton onClick={() => set({ limit: rows.length })}>Show all {rows.length}</LinkButton>
          </Box>
        ) : null}
      </>
    );
  };

  let body;
  if (grouped) {
    body = table.groups.map((g) => {
      const rows = g.rowIndexes.map((i) => table.rows[i]).filter(visible);
      if (!rows.length) return null;
      if (g.kind === "single") return renderRow(rows[0], { labelOverride: g.label });
      const open = q ? true : isGroupOpen(g, st);
      return (
        <Fragment key={g.id}>
          <ButtonBase
            onClick={() => toggleGroup(g)}
            aria-expanded={open}
            sx={{
              display: "grid",
              gridTemplateColumns: grid,
              columnGap: 1.5,
              alignItems: "center",
              width: "100%",
              textAlign: "left",
              px: 1.5,
              py: 1,
              borderTop: `1px solid ${LINE_SOFT}`,
              backgroundColor: SURFACE,
              "&:focus-visible": { ...FOCUS_RING, outlineOffset: "-2px" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <KeyboardArrowRightRounded
                sx={{ fontSize: 16, color: SUB, ml: -0.5, transition: "transform .15s", transform: open ? "rotate(90deg)" : "none" }}
              />
              <Box sx={{ fontSize: 13, fontWeight: 600, color: INK }}>{g.label}</Box>
              <Box sx={{ fontSize: 12, color: SUB, ml: 0.5 }}>
                {g.rowIndexes.length} {g.rowIndexes.length === 1 ? "item" : "items"}
              </Box>
            </Box>
            {cols.map((c) => (
              <Box key={c.key} />
            ))}
            {hasValue ? (
              <>
                <Box sx={{ fontSize: 12.5, color: SUB, textAlign: "right" }}>{g.kind === "empty" ? "" : pct(g.share)}</Box>
                <Box sx={{ fontSize: 13, fontWeight: 600, color: g.kind === "empty" ? SUB : INK, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {g.kind === "empty" ? "—" : fmtMoney(g.subtotal)}
                </Box>
              </>
            ) : null}
          </ButtonBase>
          {open ? paged(rows, g.id) : null}
        </Fragment>
      );
    });
  } else {
    const rows = [...table.rows].sort((a, b) => (st.view === "listed" ? a.index - b.index : 0)).filter(visible);
    body = paged(rows, "flat");
  }

  const noMatches = q && matches && matches.size === 0;

  return (
    <Box sx={{ px: 0.5, ...APP_FONT }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            height: 32,
            px: 1,
            border: `1px solid ${LINE}`,
            borderRadius: "8px",
            "&:focus-within": { borderColor: toneOf("accent").solid },
          }}
        >
          <SearchRounded sx={{ fontSize: 16, color: SUB }} />
          <InputBase
            value={st.query}
            onChange={(e) => set({ query: e.target.value })}
            placeholder={table.searchPlaceholder}
            inputProps={{ "aria-label": table.searchPlaceholder }}
            sx={{ flex: 1, fontSize: 12.5 }}
          />
        </Box>
        {table.groups ? (
          <Box
            role="group"
            sx={{ display: "flex", p: "3px", gap: "2px", borderRadius: "8px", border: `1px solid ${LINE}`, backgroundColor: level3.track }}
          >
            {[
              { key: "grouped", label: `By ${table.groupNoun || "group"}` },
              { key: "listed", label: "As listed" },
            ].map((v) => {
              const active = st.view === v.key;
              return (
                <ButtonBase
                  key={v.key}
                  onClick={() => set({ view: v.key })}
                  aria-pressed={active}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: "6px",
                    fontSize: 12,
                    fontWeight: active ? 600 : 500,
                    color: active ? level3.activeText : level3.restText,
                    backgroundColor: active ? level3.activeBg : "transparent",
                    boxShadow: active ? level3.activeShadow : "none",
                    "&:focus-visible": FOCUS_RING,
                  }}
                >
                  {v.label}
                </ButtonBase>
              );
            })}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "10px", overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: grid,
            columnGap: 1.5,
            px: 1.5,
            py: 0.875,
            fontSize: 11.5,
            fontWeight: 500,
            color: SUB,
            backgroundColor: SURFACE,
          }}
        >
          <Box>Item</Box>
          {cols.map((c) => (
            <Box key={c.key} sx={{ textAlign: "right" }}>{c.label}</Box>
          ))}
          {hasValue ? (
            <>
              <Box sx={{ textAlign: "right" }}>Share</Box>
              <Box sx={{ textAlign: "right" }}>{table.valueLabel}</Box>
            </>
          ) : null}
        </Box>
        {noMatches ? (
          <Box sx={{ px: 1.5, py: 2, fontSize: 12.5, color: SUB, borderTop: `1px solid ${LINE_SOFT}` }}>
            No {table.noun} match "{st.query.trim()}".
          </Box>
        ) : (
          body
        )}
        {table.total && !q ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: grid,
              columnGap: 1.5,
              alignItems: "center",
              px: 1.5,
              py: 1.125,
              borderTop: `1px solid ${LINE}`,
              backgroundColor: SURFACE,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ fontSize: 13, fontWeight: 600, color: INK }}>Total</Box>
              {table.total.matches === true ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, fontSize: 12, color: toneOf("ok").fg }}>
                  <CheckRounded sx={{ fontSize: 14 }} /> Matches stated total
                </Box>
              ) : table.total.matches === false ? (
                <Box sx={{ fontSize: 12, color: toneOf("crit").fg }}>Stated total is {table.total.expectedDisplay}</Box>
              ) : null}
            </Box>
            {cols.map((c) => (
              <Box key={c.key} />
            ))}
            <Box />
            <Box sx={{ fontSize: 13, fontWeight: 700, color: INK, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {table.total.display}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

